import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, IconButton, Modal } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const UnifiedGallery = ({ category }) => {
  const [folders, setFolders] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const scrollRefs = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get subfolders
        const foldersRes = await fetch(`${process.env.REACT_APP_API_URL}/api/subfolders/${category}`);
        
        // Validate response is JSON
        const contentType = foldersRes.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid API response format');
        }
        
        const foldersData = await foldersRes.json();
        setFolders(foldersData);

        if (foldersData.length === 0) {
          // No subfolders - fetch directly from category
          const photosRes = await fetch(`${process.env.REACT_APP_API_URL}/api/photos/${category}`);
          const photosData = await photosRes.json();
          setPhotos({ [category]: photosData });
        } else {
          // Fetch photos for each subfolder
          const photosMap = {};
          
          for (const folder of foldersData) {
            const res = await fetch(
              `${process.env.REACT_APP_API_URL}/api/photos/${category}/${folder}`
            );
            photosMap[folder] = await res.json();
          }
          
          setPhotos(photosMap);
        }
        
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [category]);

  const scroll = (folder, dir) => {
    const container = scrollRefs.current[folder];
    if (!container) return;
    const scrollAmount = container.offsetWidth * 0.8;
    container.scrollBy({ 
      left: dir === 'left' ? -scrollAmount : scrollAmount, 
      behavior: 'smooth' 
    });
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        {category.charAt(0).toUpperCase() + category.slice(1)} Photos
      </Typography>
      
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ width: '100%' }}>
          {folders.length === 0 ? (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ position: 'relative' }}>
                <Box 
                  sx={{ 
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    scrollBehavior: 'smooth',
                    px: 6,
                    py: 2,
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    minHeight: 180,
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  {photos[category]?.map(photo => (
                    <Box
                      key={photo.public_id}
                      component="img"
                      src={photo.secure_url}
                      alt=""
                      onClick={() => setSelectedPhoto(photo)}
                      sx={{
                        height: 140,
                        width: 'auto',
                        borderRadius: 2,
                        objectFit: 'cover',
                        boxShadow: 3,
                        transition: 'transform 0.2s',
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.04)' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            folders.map(folder => (
              <Box key={folder} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1, pl: 1 }}>{folder}</Typography>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    onClick={() => scroll(folder, 'left')}
                    sx={{ position: 'absolute', left: 0, top: '50%', zIndex: 2, transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 2 }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                  
                  <Box 
                    ref={(ref) => (scrollRefs.current[folder] = ref)}
                    sx={{ 
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                      scrollBehavior: 'smooth',
                      px: 6,
                      py: 2,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      minHeight: 180,
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      boxShadow: 1,
                    }}
                  >
                    {photos[folder]?.map(photo => (
                      <Box
                        key={photo.public_id}
                        component="img"
                        src={photo.secure_url}
                        alt=""
                        onClick={() => setSelectedPhoto(photo)}
                        sx={{
                          height: 140,
                          width: 'auto',
                          borderRadius: 2,
                          objectFit: 'cover',
                          boxShadow: 3,
                          transition: 'transform 0.2s',
                          cursor: 'pointer',
                          '&:hover': { transform: 'scale(1.04)' }
                        }}
                      />
                    ))}
                  </Box>
                  
                  <IconButton
                    onClick={() => scroll(folder, 'right')}
                    sx={{ position: 'absolute', right: 0, top: '50%', zIndex: 2, transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 2 }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
      
      {/* Modal for enlarged view */}
      <Modal
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          <Box
            component="img"
            src={selectedPhoto?.secure_url}
            alt=""
            sx={{
              maxHeight: '90vh',
              maxWidth: '100%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 24,
            }}
          />
          <IconButton
            onClick={() => setSelectedPhoto(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.default' }
            }}
          >
            ✕
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
};

export default UnifiedGallery;
